import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import client from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { KpiCard } from "@/components/KpiCard";
import AIInsightsPanel from "@/components/AIInsightsPanel";
import { RevenueVsBudgetChart, EbitdaMarginChart, CashFlowChart } from "@/components/Charts";
import { fmtMoney, fmtPctPlain, fmtNum } from "@/lib/format";
import { ArrowLeft, DownloadSimple, UploadSimple, CheckCircle, Clock, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_META = {
    approved: { label: "Approved", bg: "bg-fp-success/10", color: "text-fp-success", Icon: CheckCircle },
    submitted: { label: "Submitted", bg: "bg-fp-navy/10", color: "text-fp-navy", Icon: CheckCircle },
    review: { label: "In Review", bg: "bg-fp-ochre/20", color: "text-fp-ink", Icon: Clock },
    extension_granted: { label: "Extension", bg: "bg-fp-panel", color: "text-fp-secondary", Icon: Clock },
    overdue: { label: "OVERDUE", bg: "bg-fp-terracotta/15", color: "text-fp-terracotta", Icon: Warning },
    waived: { label: "Waived", bg: "bg-fp-border", color: "text-fp-muted", Icon: CheckCircle },
};

export default function EntityPortal() {
    const { entityId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const load = () => {
        setError(null);
        client.get(`/entity/${entityId}/portal`)
            .then((r) => setData(r.data))
            .catch((err) => setError(err.response?.status === 403 ? "access_denied" : "load_failed"));
    };

    useEffect(() => { setData(null); load(); }, [entityId]); // eslint-disable-line

    if (error === "access_denied") {
        return (
            <div>
                <PageHeader overline={<Link to="/" className="hover:text-fp-ink inline-flex items-center gap-1"><ArrowLeft size={11} /> Group Overview</Link>} title="Access Restricted" description={`You do not have permission to view entity ${entityId}. Entity CFOs can only access their assigned entity; Division CFOs can access entities in their division.`} />
                <div className="px-4 sm:px-6 lg:px-12 py-10">
                    <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8 max-w-2xl">
                        <div className="font-sans text-sm text-fp-secondary">Contact your Corporate Finance Manager if you believe you should have access.</div>
                    </div>
                </div>
            </div>
        );
    }
    if (error === "load_failed") {
        return <div className="p-16"><div className="font-serif text-3xl text-fp-ink">Unable to load</div><div className="font-sans text-sm text-fp-secondary mt-2">Please refresh or try again.</div></div>;
    }
    if (!data) return <div className="p-16 font-serif text-3xl text-fp-ink">Loading…</div>;

    const e = data.entity;
    const f = data.financials;
    const bs = f.balance_sheet;

    return (
        <div>
            <PageHeader
                overline={<Link to="/" className="hover:text-fp-ink inline-flex items-center gap-1"><ArrowLeft size={11} /> Group Overview</Link>}
                title={e.name}
                description={`${e.division_id} · ${e.sub_division} · ${e.country_code} · ${e.currency} · ${e.consolidation_method} consolidation · ${e.ownership_pct}% owned`}
            />

            <div className="px-4 sm:px-6 lg:px-12 py-10 space-y-10">
                {/* Submission Status */}
                <SubmissionSection subs={data.submissions} canUpload={data.can_upload} entityId={entityId} onChange={load} />

                {/* KPI Overview */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-5" data-testid="portal-kpis">
                    <KpiCard label="Revenue" value={fmtMoney(f.pnl.revenue[f.pnl.revenue.length - 1], { decimals: 0 })} subValue={`Budget: ${fmtMoney(f.pnl.budget_revenue[f.pnl.budget_revenue.length - 1], { decimals: 0 })}`} trend={f.pnl.revenue} testId="portal-kpi-revenue" />
                    <KpiCard label="EBITDA" value={fmtMoney(f.pnl.ebitda[f.pnl.ebitda.length - 1], { decimals: 0 })} subValue={`Margin: ${fmtPctPlain(f.pnl.ebitda_margin_pct[f.pnl.ebitda_margin_pct.length - 1])}`} trend={f.pnl.ebitda} />
                    <KpiCard label="Net Income" value={fmtMoney(f.pnl.net_income[f.pnl.net_income.length - 1], { decimals: 0 })} trend={f.pnl.net_income} />
                    <KpiCard label="Free Cash Flow" value={fmtMoney(f.cash_flow.free_cash_flow[f.cash_flow.free_cash_flow.length - 1], { decimals: 0 })} trend={f.cash_flow.free_cash_flow} />
                    <KpiCard label="ROIC" value={fmtPctPlain(f.returns.roic_pct)} subValue={`ROE: ${fmtPctPlain(f.returns.roe_pct)}`} />
                </div>

                {/* AI + Rev chart */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-5"><AIInsightsPanel context={`entity:${entityId}`} contextLabel={e.name} /></div>
                    <div className="lg:col-span-7 bg-white border border-fp-border p-5 sm:p-6 lg:p-8">
                        <div className="mb-6">
                            <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Revenue</div>
                            <h3 className="font-serif text-2xl text-fp-ink">Actual · Budget · Prior Year</h3>
                        </div>
                        <RevenueVsBudgetChart periods={data.financials.pnl.revenue.map((_, i) => `2025-${String(i + 2).padStart(2, "0")}`).slice(0, 12)} revenue={f.pnl.revenue} budget={f.pnl.budget_revenue} priorYear={f.pnl.prior_year_revenue} />
                    </div>
                </div>

                {/* P&L + Balance Sheet detail */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <PnLTable pnl={f.pnl} />
                    <BalanceSheetTable bs={bs} />
                </div>

                {/* Cash & EBITDA */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8">
                        <div className="mb-6"><div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Profitability</div><h3 className="font-serif text-2xl text-fp-ink">EBITDA & Margin</h3></div>
                        <EbitdaMarginChart periods={f.pnl.revenue.map((_, i) => `2025-${String(i + 2).padStart(2, "0")}`).slice(0, 12)} ebitda={f.pnl.ebitda} marginPct={f.pnl.ebitda_margin_pct} />
                    </div>
                    <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8">
                        <div className="mb-6"><div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Cash</div><h3 className="font-serif text-2xl text-fp-ink">Cash Flow</h3></div>
                        <CashFlowChart periods={f.pnl.revenue.map((_, i) => `2025-${String(i + 2).padStart(2, "0")}`).slice(0, 12)} ocf={f.cash_flow.operating_cash_flow} capex={f.cash_flow.capex} fcf={f.cash_flow.free_cash_flow} />
                    </div>
                </div>

                {/* Intercompany */}
                {data.intercompany.length > 0 && (
                    <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8" data-testid="portal-ic">
                        <div className="mb-6"><div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Intercompany</div><h3 className="font-serif text-2xl text-fp-ink">This Entity's IC Balances ({data.intercompany.length})</h3></div>
                        <table className="w-full">
                            <thead>
                                <tr className="border-b-2 border-fp-ink">
                                    <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Counterparty</th>
                                    <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Direction</th>
                                    <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Type</th>
                                    <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Amount</th>
                                    <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Mismatch</th>
                                    <th className="pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.intercompany.slice(0, 20).map((t) => (
                                    <tr key={t.id} className="border-b border-fp-border hover:bg-fp-bg">
                                        <td className="py-2.5 font-sans text-xs text-fp-ink">{t.from_entity_id === entityId ? t.to_entity_name : t.from_entity_name}</td>
                                        <td className="py-2.5 font-mono text-[10px] uppercase text-fp-secondary">{t.from_entity_id === entityId ? "Owed to us" : "We owe"}</td>
                                        <td className="py-2.5 font-sans text-xs text-fp-secondary">{t.type}</td>
                                        <td className="py-2.5 text-right font-mono text-sm text-fp-ink">{fmtMoney(t.from_entity_id === entityId ? t.reported_by_from_musd : t.reported_by_to_musd)}</td>
                                        <td className={`py-2.5 text-right font-mono text-xs ${Math.abs(t.mismatch_musd) > 0.1 ? "text-fp-terracotta" : "text-fp-success"}`}>{fmtMoney(t.mismatch_musd)}</td>
                                        <td className="py-2.5 font-mono text-[10px] uppercase text-fp-secondary">{t.status}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

const SubmissionSection = ({ subs, canUpload, entityId, onChange }) => {
    const fileRefs = { trial_balance: useRef(), statistical_kpis: useRef() };

    const download = (t) => {
        const token = localStorage.getItem("fp_token");
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/entity/${entityId}/synthetic/${t}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob()).then((blob) => {
                const link = document.createElement("a"); link.href = URL.createObjectURL(blob);
                link.download = `${t}_${entityId}.xlsx`; link.click();
                toast.success("Synthetic template downloaded");
            });
    };

    const upload = async (template, file) => {
        const fd = new FormData(); fd.append("file", file);
        const token = localStorage.getItem("fp_token");
        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/entity/${entityId}/upload/${template}`, {
                method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            if (!res.ok) { toast.error(data.detail || "Upload failed"); return; }
            toast.success(`Uploaded · ${data.rows_parsed} rows parsed`);
            onChange();
        } catch (e) { toast.error("Upload failed"); }
    };

    return (
        <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8" data-testid="portal-submissions">
            <div className="mb-6"><div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Monthly Cycle</div><h3 className="font-serif text-2xl text-fp-ink">Submission Status</h3></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {subs.map((sub) => {
                    const st = STATUS_META[sub.status] || STATUS_META.submitted;
                    const Icon = st.Icon;
                    return (
                        <div key={sub.id} className="border border-fp-border p-5" data-testid={`portal-sub-${sub.template}`}>
                            <div className="flex items-baseline justify-between mb-3">
                                <div>
                                    <div className="font-sans text-sm text-fp-ink font-medium">{sub.template_label}</div>
                                    <div className="font-mono text-[10px] text-fp-muted uppercase tracking-widest mt-0.5">Cycle {sub.cycle_period}</div>
                                </div>
                                <span className={`inline-flex items-center gap-1 px-2.5 py-1 ${st.bg} ${st.color} font-sans text-xs`}>
                                    <Icon size={12} weight="fill" />{st.label}
                                </span>
                            </div>
                            <div className="font-mono text-xs text-fp-secondary space-y-0.5 mb-4">
                                <div>Preparer: {sub.preparer.slice(0, 40)}</div>
                                <div>Submitted: {sub.submitted_at ? new Date(sub.submitted_at).toLocaleString() : "—"}</div>
                                {sub.approver && <div>Approver: {sub.approver}</div>}
                                {sub.days_late > 0 && <div className="text-fp-terracotta">Late by {sub.days_late} days</div>}
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button data-testid={`dl-${sub.template}`} onClick={() => download(sub.template)} className="inline-flex items-center gap-1.5 border border-fp-ink text-fp-ink px-3 py-1.5 text-xs hover:bg-fp-panel">
                                    <DownloadSimple size={12} /> Download Template
                                </button>
                                {canUpload && (
                                    <>
                                        <input ref={fileRefs[sub.template]} type="file" accept=".xlsx" className="hidden" onChange={(e) => e.target.files[0] && upload(sub.template, e.target.files[0])} />
                                        <button data-testid={`up-${sub.template}`} onClick={() => fileRefs[sub.template].current?.click()} className="inline-flex items-center gap-1.5 bg-fp-ink text-white px-3 py-1.5 text-xs hover:bg-fp-ink-hover">
                                            <UploadSimple size={12} /> Upload File
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            {!canUpload && (
                <div className="mt-4 font-sans text-xs text-fp-muted">Read-only access · Only the Entity CFO, Division CFO or Corporate Finance can upload.</div>
            )}
        </div>
    );
};

const PnLTable = ({ pnl }) => {
    const last = -1;
    const rows = [
        ["Revenue", pnl.revenue[last], pnl.budget_revenue[last], "top"],
        ["COGS", -pnl.cogs[last], null, ""],
        ["Gross Profit", pnl.gross_profit[last], null, "sub"],
        ["OpEx", -pnl.opex[last], null, ""],
        ["EBITDA", pnl.ebitda[last], null, "sub"],
        ["Depreciation", -pnl.depreciation[last], null, ""],
        ["EBIT", pnl.ebit[last], null, "sub"],
        ["Net Income", pnl.net_income[last], null, "bold"],
    ];
    return (
        <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8" data-testid="portal-pnl">
            <div className="mb-6"><div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Latest Month</div><h3 className="font-serif text-2xl text-fp-ink">Profit & Loss</h3></div>
            <table className="w-full">
                <thead><tr className="border-b-2 border-fp-ink">
                    <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Line</th>
                    <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Actual</th>
                    <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Budget</th>
                </tr></thead>
                <tbody>
                    {rows.map(([label, v, budget, kind], i) => (
                        <tr key={i} className={`border-b border-fp-border ${kind === "sub" ? "bg-fp-panel/50" : ""} ${kind === "bold" ? "border-t-2 border-fp-ink" : ""}`}>
                            <td className={`py-2.5 font-sans text-sm ${kind === "bold" || kind === "sub" ? "font-medium text-fp-ink" : "text-fp-secondary"}`}>{label}</td>
                            <td className={`py-2.5 text-right font-mono text-sm ${kind === "bold" ? "text-fp-ink font-bold" : "text-fp-ink"}`}>{fmtMoney(v)}</td>
                            <td className="py-2.5 text-right font-mono text-sm text-fp-muted">{budget ? fmtMoney(budget) : ""}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const BalanceSheetTable = ({ bs }) => {
    const items = [
        ["Cash & Equivalents", bs.cash, "asset"],
        ["Accounts Receivable", bs.accounts_receivable, "asset"],
        ["Inventory", bs.inventory, "asset"],
        ["Total Assets", bs.total_assets, "total"],
        ["Accounts Payable", bs.accounts_payable, "liab"],
        ["Debt", bs.debt, "liab"],
        ["Total Liabilities", bs.total_liabilities, "total"],
        ["Equity", bs.equity, "total"],
        ["Working Capital", bs.working_capital, "sub"],
        ["Debt / Equity", bs.debt_to_equity, "ratio"],
        ["Current Ratio", bs.current_ratio, "ratio"],
    ];
    return (
        <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8" data-testid="portal-bs">
            <div className="mb-6"><div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Snapshot</div><h3 className="font-serif text-2xl text-fp-ink">Balance Sheet</h3></div>
            <table className="w-full">
                <tbody>
                    {items.map(([label, v, kind], i) => (
                        <tr key={i} className={`border-b border-fp-border ${kind === "total" ? "border-b-2 border-fp-ink font-semibold" : ""}`}>
                            <td className={`py-2.5 font-sans text-sm ${kind === "total" ? "text-fp-ink font-medium" : "text-fp-secondary"}`}>{label}</td>
                            <td className="py-2.5 text-right font-mono text-sm text-fp-ink">{kind === "ratio" ? v.toFixed(2) : fmtMoney(v)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
